import { Component, type ErrorInfo, type ReactNode } from 'react';

interface HeartbeatErrorBoundaryProps {
	children: ReactNode;
}

interface HeartbeatErrorBoundaryState {
	hasError: boolean;
}

/**
 * Error boundary component for the Heartbeat feature.
 * Catches JavaScript errors in the component tree and provides graceful degradation.
 *
 * If the heartbeat component fails, it fails silently rather than crashing the entire application.
 * This is appropriate since the heartbeat is a non-critical enhancement feature.
 *
 * Note: Uses class component as required for error boundaries in React.
 * This is an exception to the function component preference for error handling.
 */
// eslint-disable-next-line @repo/internal/react/no-class-components
class HeartbeatErrorBoundary extends Component<
	HeartbeatErrorBoundaryProps,
	HeartbeatErrorBoundaryState
> {
	constructor(props: HeartbeatErrorBoundaryProps) {
		super(props);
		this.state = { hasError: false };
	}

	static getDerivedStateFromError(_error: Error): HeartbeatErrorBoundaryState {
		// Update state to trigger fallback UI
		return { hasError: true };
	}

	componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
		// In production, this would likely send to a monitoring service
		// eslint-disable-next-line no-console
		console.error('Heartbeat component error:', {
			error: error.message,
			stack: error.stack,
			componentStack: errorInfo.componentStack,
		});
	}

	render(): ReactNode {
		if (this.state.hasError) {
			// Graceful degradation: render nothing
			// The heartbeat banner is an enhancement, not critical functionality
			return null;
		}

		return this.props.children;
	}
}

export default HeartbeatErrorBoundary;
